import React, { useState, useEffect } from 'react';
import Measure, { MeasuredComponentProps } from 'react-measure';

import { UserListRow } from './UserListRow';
import { classNames } from '@fullstory/util/fmt';
import { SessionExpanderRoute } from '@fullstory/routes';
import { Divider } from '@fullstory/components';
import css from './userList.module.scss';
import { EventMarker, SearchUser } from '@fullstory/proto/fs/services/alexa';

const userListLoadingTimeout = 100;
const userListThreeColumnBreakpoint = 450;

export interface UserListItem {
  user: SearchUser;
  sessionLink: SessionExpanderRoute;
  eventMarkers: EventMarker[];
  lastSessionWatched: boolean;
  gravatarURL?: string;
}

interface UserListProps {
  users: UserListItem[];
  /**
   * Whether the list should display a loading state.
   * The list will wait a short interval after receiving this prop
   * before actually rendering the loading state, to avoid flashing
   * if loading the next set of users doesn't take long.
   */
  loading: boolean;
  orgId: string;
  fetchSessionMarkers?: boolean;
  onDetailClick?: (individId: string) => void;
  onUserInfoClick: () => void;
  onPlayClick?: () => void;
  completeSessions: boolean;
  newTab?: boolean;
  autoSelect?: boolean;
}

// How often to refresh time-dependent info in the user list, such as the
// online indicators.
const refreshInterval = 5 * 60 * 1000; // milliseconds

const useNowDate = (refreshInterval: number) => {
  const [nowDate, setNowDate] = useState(new Date());
  useEffect(() => {
    const intervalId = setInterval(() => {
      setNowDate(new Date());
    }, refreshInterval);
    return () => clearInterval(intervalId);
  }, [refreshInterval]);
  return nowDate;
};

const useOptimisticLoading = (loading: boolean, delay: number = 100) => {
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    let delayTimeout: ReturnType<typeof setTimeout>;
    if (loading) {
      // We may need to render the loading state.
      // Kick off the delay if we haven't already.
      delayTimeout = setTimeout(() => {
        setIsLoading(true);
      }, delay);
    } else {
      // We are not loading. Remove the loading state if we
      // were previously showing it.
      setIsLoading(false);
    }
    return () => {
      if (delayTimeout) clearTimeout(delayTimeout);
    };
  }, [loading, delay]);
  return isLoading;
};

export const UserList: React.FC<UserListProps> = ({
  loading,
  onPlayClick,
  onUserInfoClick,
  fetchSessionMarkers,
  users,
  orgId,
  completeSessions,
  newTab,
  autoSelect,
}) => {
  const now = useNowDate(refreshInterval);
  const isLoading = useOptimisticLoading(loading, userListLoadingTimeout);
  return (
    <Measure data-component="UserList" bounds>
      {({ measureRef, contentRect }: MeasuredComponentProps) => (
        <div
          className={classNames('userList', css.userList, {
            loading: isLoading,
            [css.loading]: isLoading,
          })}
          ref={measureRef}
        >
          {users &&
            users.map(({ user, ...rowProps }, idx) => {
              let parsedUser = { ...user };
              if (fetchSessionMarkers) {
                parsedUser = { ...user, fetchSessionMarkers: true };
              }
              return (
                <React.Fragment key={parsedUser.indvId as string}>
                  {idx !== 0 ? <Divider /> : null}
                  <UserListRow
                    orgId={orgId}
                    user={parsedUser}
                    {...rowProps}
                    now={now}
                    onPlayClick={onPlayClick}
                    onUserInfoClick={onUserInfoClick}
                    smallView={(contentRect.bounds?.width ?? 0) < userListThreeColumnBreakpoint}
                    completeSessions={completeSessions}
                    newTab={newTab}
                    autoSelect={autoSelect}
                  />
                </React.Fragment>
              );
            })}
        </div>
      )}
    </Measure>
  );
};
